/**
 * What a client reads about their requests on their own data (S-32): the name of each right, where a
 * request stands, and the dates, in plain words. Never a status code, never a ground code.
 */
import type { DataRequest, DataRight } from '../services/dataRequests.service';

export const RIGHT_LABEL: Record<DataRight, string> = {
  access: 'See my data',
  rectification: 'Correct my data',
  cancellation: 'Delete my data',
  opposition: 'Object to a use of my data',
};

export const RIGHT_HELP: Record<DataRight, string> = {
  access: 'We give you a copy of the personal data we hold about you.',
  rectification: 'You tell us what is wrong or out of date and why, and we correct it.',
  cancellation:
    'We stop using your data and set it aside. Records the law requires us to keep, such as those of your contracts and contributions, are not deleted: they are kept only for legal purposes for the period the law sets.',
  opposition: 'You ask us to stop using your data for a purpose, giving the reason.',
};

export const RIGHTS: readonly DataRight[] = ['access', 'rectification', 'cancellation', 'opposition'];

/** Where the request stands, from the client's side. */
export function standing(request: Pick<DataRequest, 'status' | 'determination'>): string {
  switch (request.status) {
    case 'received':
      return 'Received';
    case 'answered':
      return request.determination === 'partial'
        ? 'Answered, partly accepted: being carried out'
        : 'Answered: being carried out';
    case 'executed':
      return 'Done';
    default:
      return 'Answered';
  }
}

/** The line about the date: when we answer, or when it will be carried out. */
export function dateLine(
  request: Pick<DataRequest, 'status' | 'answer_due_on' | 'execute_due_on' | 'extended'>,
  formatDay: (day: string) => string,
): string | null {
  if (request.status === 'received') {
    return request.extended
      ? `We will answer by ${formatDay(request.answer_due_on)}. We extended the period once, as the law allows.`
      : `We will answer by ${formatDay(request.answer_due_on)}.`;
  }
  if (request.status === 'answered' && request.execute_due_on) {
    return `We will carry it out by ${formatDay(request.execute_due_on)}.`;
  }
  return null;
}

/** A request the client may still wait on: one per right is open at a time. */
export function isOpen(request: Pick<DataRequest, 'status' | 'determination'>): boolean {
  return request.status === 'received' || request.status === 'answered';
}

/** The rights the client has no open request for. */
export function rightsToAsk(
  requests: readonly Pick<DataRequest, 'right' | 'status' | 'determination'>[],
): DataRight[] {
  const open = new Set(requests.filter(isOpen).map((r) => r.right));
  return RIGHTS.filter((right) => !open.has(right));
}

/** The right the form shows, explains and sends: the one picked while it is still offered, else the
 *  first one offered. The offer changes when the open requests load and after each request is sent. */
export function shownRight(picked: DataRight, offered: readonly DataRight[]): DataRight {
  return offered.includes(picked) ? picked : offered[0];
}
